import {
  UseMutateAsyncFunction,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { UserProfileDto, UserRole } from "@green0meter/api-client";
import { AxiosResponse } from "axios";

import { DOMAIN_FORMS_CONFIG } from "../domain";

import { formsConfigApi } from "@api/services";
import {
  ACTION_GET_FORM_SECTIONS,
  ACTION_GET_FORMS_CONFIGS,
  DOMAIN_FORMS,
  roleGuard,
} from "@api";

export const ACTION_IMPORT_USER_FORM_CONFIG = "IMPORT_USER_FORM_CONFIG";

const allowedRoles = [UserRole.Owner, UserRole.Manager, UserRole.Respondent];

type ImportUserFormConfigRequest = {
  formId: string;
  file: File;
};

const importUserFormConfig = async (
  req: ImportUserFormConfigRequest,
): Promise<AxiosResponse<void>> =>
  await formsConfigApi.formsConfigControllerImportUserFormConfigJson(
    req.formId,
    req.file,
  );

export const useImportUserFormConfig = (
  user: UserProfileDto | null,
  formId?: string,
): {
  importUserFormConfigAsync: UseMutateAsyncFunction<
    AxiosResponse<void>,
    Error,
    ImportUserFormConfigRequest,
    unknown
  >;
  importUserFormConfigEnabled: boolean;
  isSuccessImportUserFormConfig: boolean;
  isPendingImportUserFormConfig: boolean;
  isErrorImportUserFormConfig: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutateAsync, isSuccess, isPending, isError } = useMutation({
    mutationKey: [DOMAIN_FORMS_CONFIG, ACTION_IMPORT_USER_FORM_CONFIG],
    mutationFn: enabled ? importUserFormConfig : undefined,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_FORMS_CONFIG, ACTION_GET_FORMS_CONFIGS],
      });
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_FORMS, ACTION_GET_FORM_SECTIONS, formId],
      });
    },
  });

  return {
    importUserFormConfigAsync: mutateAsync,
    importUserFormConfigEnabled: enabled,
    isSuccessImportUserFormConfig: isSuccess,
    isPendingImportUserFormConfig: isPending,
    isErrorImportUserFormConfig: isError,
  };
};
